import { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { ActivityIndicator, Text } from "react-native-paper";
import { Image, Platform, ScrollView, TouchableOpacity, View } from "react-native";
import { cond, stubTrue } from "lodash";

import { Button, Modal } from "../../components";
import { Chip as ChipStyled } from "../../components/styled";
import { readableDate } from "../../utils/dateUtils";
import { EVENT_DETAILS_FRAGMENT, EVENT_ELIGIBILITY_QUERY, EVENT_REGISTRATION_QUERY } from "./queries";
import { EventDetailsQuery, EventEligibilityQuery } from "./types";

import CalendarIcon from "../../svgs/CalendarIcon";
import MapPinIcon from "../../svgs/MapPinIcon";
import CheckSquareIcon from "../../svgs/CheckSquareIcon";
import { LinearGradient } from "expo-linear-gradient";
import { afterRegistrationClose, beforeRegistrationOpen } from "./helpers";
import HTMLDisplay from "../../components/HTMLDisplay";
import { colors } from "../../../theme";
import { ScreenProps } from "../types";
import { useIntercomClient } from "../../hooks/useIntercom";

const bannerImage = require("../../../assets/masters-banner.jpeg");

export const EVENT_DETAILS_QUERY = gql`
  query EventDetailsQuery($slug: String!) {
    event(slug: $slug) {
      ...EventDetailsFragment
    }
  }
  ${EVENT_DETAILS_FRAGMENT}
`;

const EventDetailsBanner = ({ event }: { event: EventDetailsQuery["event"] }) => {
  return (
    <View className="w-full lg:rounded-lg" style={{ overflow: "hidden" }}>
      <Image source={{ uri: event?.bannerImageUrl || bannerImage }} className="w-full h-[350px]" />
      <LinearGradient
        colors={["rgba(0, 0, 0, 0.55)", "rgba(76, 76, 76, 0)"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        locations={[0.099, 0.9721]}
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
      />
      <View className="absolute top-[70px] left-[20px] md:top-[90px] md:left-[97px] px-4">
        <Text className="text-title-2 md:text-title-1 mt-10 text-white font-700">{event?.title}</Text>
        <Text className="mt-2 text-white font-700 text-regular-normal md:text-title-4">
          {readableDate(new Date(event?.startsAt))} – {readableDate(new Date(event?.endsAt))}
        </Text>
      </View>
    </View>
  );
};

export const EventDetailsCard = ({
  event,
  eligibilityErrors,
  registrationEligible,
  registered,
  onRegistrationPress,
  onTicketPress,
}: {
  event: EventDetailsQuery["event"];
  eligibilityErrors: string[];
  registrationEligible: boolean;
  registered: boolean;
  onRegistrationPress: () => void;
  onTicketPress: () => void;
}) => {
  const [visible, setVisible] = useState<boolean>(false);

  const RegisterStatus = cond([
    [
      () => registered,
      () => (
        <ChipStyled className="rounded-2xl bg-green-lightest" textStyle="my-0.5" testID="register-status">
          <Text className="text-ink font-400">Already Registered</Text>
        </ChipStyled>
      ),
    ],
    [
      beforeRegistrationOpen,
      () => (
        <ChipStyled className="rounded-2xl bg-sky-light" textStyle="my-0.5" testID="register-status">
          <Text className="text-ink font-400">Registration Opening Soon</Text>
        </ChipStyled>
      ),
    ],
    [
      afterRegistrationClose,
      () => (
        <ChipStyled className="rounded-2xl bg-sky-light" textStyle="my-0.5" testID="register-status">
          <Text className="text-ink font-400">Registration Closed</Text>
        </ChipStyled>
      ),
    ],
    [
      () => !!event.registrationFull,
      () => (
        <ChipStyled className="rounded-2xl bg-yellow-lightest" textStyle="my-0.5" testID="register-status">
          <Text className="text-yellow-dark font-400">Registration Full</Text>
        </ChipStyled>
      ),
    ],
    [
      () => !registrationEligible,
      () => (
        <ChipStyled className="rounded-2xl bg-sky-light" textStyle="my-0.5" testID="register-status">
          <Text className="text-ink font-400">Registration Unavailable</Text>
        </ChipStyled>
      ),
    ],
    [
      stubTrue,
      () => (
        <ChipStyled className="rounded-2xl bg-green-lightest" textStyle="my-0.5" testID="register-status">
          <Text className="text-green-dark font-500">Registration Open</Text>
        </ChipStyled>
      ),
    ],
  ]);

  const EligibilityModal = ({ visible, setVisible }: { visible: boolean; setVisible: (value: boolean) => void }) => {
    const intercom = useIntercomClient();
    const handleContactMembershipServices = () => {
      intercom.presentMessageComposer();
    };

    return (
      <Modal
        {...{
          visible,
          title: "Eligibility Issues",
          onDismiss: () => setVisible(false),
        }}
      >
        <View className="m-6" testID="ineligible-modal">
          {eligibilityErrors.map((item, idx) => (
            <Text key={idx} className="bg-sky-white text-regular-normal font-500 pb-2">
              {item}.
            </Text>
          ))}
          <Text className="text-regular-normal font-500 pb-2">
            If you feel this is an error please{" "}
            <Text className="underline text-primary font-500" onPress={handleContactMembershipServices}>
              contact member services.
            </Text>
          </Text>
        </View>
      </Modal>
    );
  };

  const RegisterButton = cond([
    [
      () => registered,
      () => (
        <Button mode="contained" testID="register-button" onPress={onTicketPress}>
          View Registration
        </Button>
      ),
    ],
    [
      beforeRegistrationOpen,
      (event) => (
        <Button mode="contained" disabled testID="register-button">
          <View>
            <View>Registration Opens </View>
            <View>{readableDate(new Date(event.registrationOpenAt))}</View>
          </View>
        </Button>
      ),
    ],
    [
      afterRegistrationClose,
      () => (
        <Button mode="contained" disabled testID="register-button">
          Registration Closed
        </Button>
      ),
    ],
    [
      () => !!event.registrationFull,
      () => (
        <View>
          <Button mode="contained" disabled testID="register-button">
            Registration Full
          </Button>
          <Text className="text-small-normal text-center mt-3 font-500" testID="ineligible-text">
            Event has reached maximum capacity
          </Text>
        </View>
      ),
    ],
    [
      () => !registrationEligible,
      () => (
        <View>
          <Button mode="contained" testID="register-button" onPress={() => setVisible(true)} isValid={false}>
            Registration Unavailable
          </Button>
          <Text className="text-small-normal text-center mt-3 font-500" testID="ineligible-text">
            <TouchableOpacity onPress={() => setVisible(true)} testID="ineligible-link">
              <Text className="text-primary font-500 underline">Why can't I register?</Text>
            </TouchableOpacity>
            <EligibilityModal {...{ visible, setVisible }} />
          </Text>
        </View>
      ),
    ],
    [
      stubTrue,
      () => (
        <Button mode="contained" testID="register-button" onPress={onRegistrationPress}>
          Register
        </Button>
      ),
    ],
  ]);
  return (
    <View
      className="py-6 px-1 w-[328px] md:w-[367px] md:min-w-[367px] shadow-md rounded-lg bg-white border-sky"
      testID="event-details"
    >
      <View className="px-6">
        <View className="mt-2 mb-3">
          <View className="flex-row items-center justify-start">{RegisterStatus(event)}</View>
        </View>
        <View className="divide-y divide-sky-light">
          <View className="flex flex-row py-3">
            <CalendarIcon height={22} width={22} />
            <View className="pl-2">
              <Text className="text-small-normal font-700 text-ink-darker">Event Date & Time</Text>
              <Text className="text-small-normal text-ink-darker font-400">
                {readableDate(new Date(event?.startsAt))} - {readableDate(new Date(event?.endsAt))}
              </Text>
            </View>
          </View>
          <View className="flex flex-row py-3">
            <MapPinIcon height={22} width={22} />
            <View className="pl-2">
              <Text className="text-small-normal font-700 text-ink-darker">Location</Text>
              <Text className="text-small-normal text-ink-darker font-400">{event?.location}</Text>
              <Text className="text-small-normal text-ink-darker font-400">{event?.address?.address1}</Text>
              <Text className="text-small-normal text-ink-darker font-400">{`${event?.address?.city}, ${event?.address?.state} ${event?.address?.zip}`}</Text>
            </View>
          </View>
          <View className="flex flex-row py-3">
            <CheckSquareIcon height={22} width={22} />
            <View className="pl-2">
              <Text className="text-small-normal font-700 text-ink-darker">Registration Info</Text>
              {beforeRegistrationOpen(event) && (
                <Text className="text-small-normal text-ink-darker font-400" testID="registration-open">{`Opens: ${readableDate(
                  new Date(event?.registrationOpenAt)
                )}`}</Text>
              )}
              <Text className="text-small-normal text-ink-darker font-400" testID="registration-close">{`Closes: ${readableDate(
                new Date(event?.registrationCloseAt)
              )}`}</Text>
            </View>
          </View>
        </View>
        <View className="mt-3">{RegisterButton(event)}</View>
      </View>
    </View>
  );
};

export const EventDetailsScreen = ({ navigation, route }: ScreenProps<"EventDetails">) => {
  const { data, loading } = useQuery<EventDetailsQuery>(EVENT_DETAILS_QUERY, {
    variables: { slug: route.params.slug },
  });
  const { data: eligibilityData } = useQuery<EventEligibilityQuery>(EVENT_ELIGIBILITY_QUERY, {
    variables: { slug: route.params.slug },
  });
  const { data: registrationData } = useQuery(EVENT_REGISTRATION_QUERY, {
    variables: { slug: route.params.slug },
  });
  const registrationEligible = !!eligibilityData?.eventEligibility?.isEligible;
  const eligibilityErrors = eligibilityData?.eventEligibility?.eligibilityErrors || [];
  const registered = !!registrationData?.eventRegistration?.registeredAt;

  if (loading) {
    return <ActivityIndicator className="flex-1 items-center justify-center" testID="query-loader" />;
  }
  const { event } = data || {};

  const onRegistrationPress = () => navigation.navigate("EventRegistration", { slug: route.params.slug });
  const onTicketPress = () => navigation.navigate("EventConfirmation", { slug: route.params.slug });

  const children = event && (
    <View className="relative sm:w-full md:w-full lg:max-w-[1200px] md:mx-auto lg:mt-6">
      <View className="w-full">
        <EventDetailsBanner event={event} />
      </View>
      <View className="w-full flex flex-col flex-col-reverse lg:flex-row">
        <View className="px-8 mt-9 md:mb-8 lg:w-2/3">
          <Text className="text-title-4 font-700 md:mt-8 mb-4">Event Details</Text>
          <HTMLDisplay html={event?.description} fontSize={16} lineHeight={24} color={colors.ink.dark} />
        </View>
        <View className={"justify-center items-center mt-[-60px] max-h-[438px]"}>
          <EventDetailsCard
            event={event}
            eligibilityErrors={eligibilityErrors}
            registrationEligible={registrationEligible}
            registered={registered}
            onRegistrationPress={onRegistrationPress}
            onTicketPress={onTicketPress}
          />
        </View>
      </View>
    </View>
  );

  return (
    <View className="bg-white h-full min-h-full">{Platform.OS === "web" ? children : <ScrollView>{children}</ScrollView>}</View>
  );
};

export default EventDetailsScreen;
